/**
 * マニュアルの HTML を、画面に出して安全な形へ削る。
 *
 * マニュアルは FMT の編集画面から手で書く。書くのは身内だけだが、HTML をそのまま
 * 描くと、貼り付けた中身にスクリプトが紛れていたときに全員の画面で動いてしまう。
 * 「出してよいタグ・属性」を並べ、それ以外は落とす方式にして、書き手が気づかずに
 * 危ないものを持ち込んでも実行されないようにする。
 *
 * 外部ライブラリは使わない。ブラウザの `DOMParser` は読み込むだけで何も実行せず
 * （画像も取りに行かない）、この用途にはそれで足りるため。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business マニュアルの HTML から、出してよいタグと属性だけを残す。
 * @graph-connects none
 */

/** 残すタグ。見出し・箇条書き・表・リンクなど、手順書に要るものだけ */
/** @graph-connects none */
const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

/**
 * 中身ごと消すタグ。
 *
 * 残さないタグは原則「タグだけ外して中身は残す」が、これらは中身そのものが
 * 画面に出てはいけない（`<script>` の本体、`<style>` の指定など）
 * @graph-connects none
 */
const DROPPED_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'noscript',
  'template',
  'link',
  'meta',
  'base',
]);

/** タグごとに残す属性。ここに無い属性は落とす（`onclick` などが入り込まないように） */
/** @graph-connects none */
const ALLOWED_ATTRIBUTES: Record<string, readonly string[]> = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'title'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan'],
};

/**
 * 開いてよいリンク先。
 *
 * `http(s)` とメール、ページ内（`#`）、同じ場所の絶対パス（`/`）だけを通す。
 * ただし `/` 始まりは 1 本だけに限る。`//evil.example.com` は「プロトコル相対 URL」で、
 * 見た目は相対パスなのに別のホストへ飛ぶ。ブラウザは URL の中の `\` を `/` に直すので
 * `/\evil.example.com` も同じ扱いになり、どちらも弾く必要がある
 * @graph-connects none
 */
const SAFE_URL = /^(?:https?:\/\/|mailto:|#|\/(?![/\\]))/i;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 属性の値がリンク先として開いてよいものかを判定する。別のホストへ飛ぶ書き方は通さない
 * @graph-connects none
 */
function isSafeUrl(value: string): boolean {
  // `java\nscript:` のように空白や改行を挟んで判定をすり抜ける書き方があるので、先に落とす。
  // 落とすのは 0x20 以下だけで、絵文字などを分断することはない
  let packed = '';
  for (let index = 0; index < value.length; index += 1) {
    if (value.charCodeAt(index) > 0x20) {
      packed += value[index];
    }
  }
  return SAFE_URL.test(packed);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 要素から、残してよい属性だけを残す
 * @graph-connects isSafeUrl [calls] リンク先が安全かを確かめる
 */
function stripAttributes(element: Element): void {
  const allowed = ALLOWED_ATTRIBUTES[element.tagName.toLowerCase()] ?? [];
  for (const name of [...element.getAttributeNames()]) {
    const value = element.getAttribute(name) ?? '';
    const keep =
      allowed.includes(name) && ((name !== 'href' && name !== 'src') || isSafeUrl(value));
    if (!keep) {
      element.removeAttribute(name);
    }
  }
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 要素の木をたどり、残さないタグを外しながら属性を削る
 * @graph-connects stripAttributes [calls] 残してよい属性だけにする
 */
function clean(node: Element): void {
  // 子を先に片付ける。親を外すと、その場で子の位置が変わるため
  for (const child of [...node.children]) {
    clean(child);
  }

  const tag = node.tagName.toLowerCase();
  if (DROPPED_WITH_CONTENT.has(tag)) {
    node.remove();
    return;
  }
  if (!ALLOWED_TAGS.has(tag)) {
    // タグだけ外して中身は残す。書いた文章が黙って消えると気づけないため
    node.replaceWith(...node.childNodes);
    return;
  }
  stripAttributes(node);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business マニュアルの HTML から、出してよいタグと属性だけを残して返す
 * @graph-connects clean [calls] 木をたどって削る
 */
export function sanitizeHtml(html: string): string {
  // DOMParser は読み込むだけで、スクリプトも画像の取得も走らせない
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  for (const child of [...parsed.body.children]) {
    clean(child);
  }
  return parsed.body.innerHTML;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business マニュアルに中身があるかを判定する。空のタグだけのときは「無い」とみなす
 * @graph-connects none
 */
export function hasManual(html: string | undefined): boolean {
  if (html === undefined || html.trim() === '') {
    return false;
  }
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return (parsed.body.textContent ?? '').trim() !== '' || parsed.body.querySelector('img') !== null;
}
